<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AltArrowDownIcon, BrainIcon } from '@solar-icons/vue/linear'
import AppFavicon from '../../primitives/AppFavicon/AppFavicon.vue'
import AppLiveText from '../../primitives/AppLiveText/AppLiveText.vue'
import AppParticleRing from '../../primitives/AppParticleRing/AppParticleRing.vue'
import { vPointerLight } from '../../primitives/v-pointer-light'
import type { UseDisclosureTriggerAttrs } from '../../../composables/useDisclosure'
import { ACTIVITY_KIND_META } from './activity-thread-types'
import type { ActivityStackIcon } from './activity-summary'
import type { AgentSource } from '@/types/chat'

const FAVICON_LIMIT = 3

const props = defineProps<{
  open: boolean
  isStreaming: boolean
  /** Caption of the step running right now; wins over the held opener caption while streaming. */
  activeLabel?: string
  /** Elapsed run time in seconds, shown in the summary once streaming has finished. */
  duration?: number
  /** Number of steps in the run, shown in the summary once streaming has finished. */
  stepCount?: number
  /** Number of steps that failed, added to the summary when there are any. */
  failedCount?: number
  /** One icon per kind of step the run took, stacked beside the summary once streaming has finished and no web page was read. */
  stack?: readonly ActivityStackIcon[]
  /** The web pages the run read; once streaming has finished, their site icons and a count replace the kind icons. */
  sources?: readonly AgentSource[]
  /** Explicit header label; overrides both the live signal and the summary label. */
  label?: string
  triggerAttrs?: UseDisclosureTriggerAttrs
}>()

defineEmits<{
  toggle: []
}>()

const { t } = useI18n()

const lastRealCaption = ref<string | undefined>()
const stackArrives = ref(false)

watch(
  () => props.isStreaming,
  (streaming, wasStreaming) => {
    if (streaming) lastRealCaption.value = undefined
    stackArrives.value = !streaming && wasStreaming
  },
)

watch(
  () => props.activeLabel,
  (label) => {
    if (label) lastRealCaption.value = label
  },
  { immediate: true },
)

const summary = computed(() => {
  const count = props.stepCount ?? 0
  const worked =
    props.duration === undefined
      ? t('chat.activity.workedSummaryUnknownDuration', { count })
      : t('chat.activity.workedSummary', { count, seconds: props.duration })
  const failed = props.failedCount ?? 0
  return failed > 0
    ? t('chat.activity.summaryWithFailures', {
        summary: worked,
        failed: t('chat.activity.failedCount', { count: failed }),
      })
    : worked
})

const caption = computed(() => {
  if (props.label) return props.label
  if (props.isStreaming)
    return props.activeLabel ?? lastRealCaption.value ?? t('chat.activity.opener')
  return summary.value
})

const faviconStack = computed(() => (props.sources ?? []).slice(0, FAVICON_LIMIT))
const hasSources = computed(() => (props.sources?.length ?? 0) > 0)
</script>

<template>
  <button
    v-pointer-light
    type="button"
    v-bind="triggerAttrs"
    class="group pointer-glow control-press press-wide flex min-h-20 w-full min-w-0 items-center gap-4 rounded-6 px-8 py-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue"
    :class="isStreaming ? undefined : 'text-text-secondary hovered:text-text-primary'"
    @click="$emit('toggle')"
  >
    <AppParticleRing v-if="isStreaming" variant="mark" />
    <BrainIcon v-else class="shrink-0 text-icon-secondary" />

    <span class="min-w-0 flex-1" aria-live="polite">
      <AppLiveText
        :text="caption"
        line-class="truncate text-body-sm"
        :text-class="isStreaming ? 'text-shimmer-ink motion-safe:animate-shimmer-ink' : ''"
      />
    </span>

    <span
      v-if="!isStreaming && hasSources"
      data-part="sources"
      aria-hidden="true"
      class="flex shrink-0 items-center gap-4"
    >
      <span class="flex">
        <span
          v-for="(source, index) in faviconStack"
          :key="source.ref"
          class="rounded-2 bg-bg-surface shadow-contact not-first:-ml-1"
          :class="{ 'activity-stack-in': stackArrives }"
          :style="stackArrives ? { animationDelay: `${200 + index * 60}ms` } : undefined"
        >
          <AppFavicon :src="source.favicon" kind="web" />
        </span>
      </span>
      <span class="text-caption text-text-tertiary tabular-nums">{{
        t('chat.sources.count', sources?.length ?? 0)
      }}</span>
    </span>
    <span
      v-else-if="!isStreaming && stack?.length"
      data-part="stack"
      aria-hidden="true"
      class="icon-sm flex shrink-0"
    >
      <span
        v-for="(icon, index) in stack"
        :key="icon.kind"
        class="grid size-12 place-items-center rounded-full border border-border bg-bg-secondary not-first:-ml-2"
        :class="[
          icon.failed ? 'text-accent-red' : 'text-text-secondary',
          { 'activity-stack-in': stackArrives },
        ]"
        :style="stackArrives ? { animationDelay: `${200 + index * 60}ms` } : undefined"
      >
        <component :is="ACTIVITY_KIND_META[icon.kind].icon" />
      </span>
    </span>

    <AltArrowDownIcon
      class="shrink-0 text-icon-tertiary transition-transform duration-slower ease-spring-bouncy"
      :class="open ? 'rotate-0' : '-rotate-90'"
    />
  </button>
</template>
